import React, { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Download, FileText, Link2, Loader2, Plus, Sparkles, Target, X } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

export default function ProfilingPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const forClient = params.get("client_id");
  const q = forClient ? { params: { client_id: forClient } } : {};
  const [data, setData] = useState(null);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [careers, setCareers] = useState({});
  const [slot, setSlot] = useState(0);
  const [saving, setSaving] = useState(false);
  const [fetching, setFetching] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [ai, setAi] = useState(null);

  useEffect(() => {
    api.get("/profiling/me", q).then(({ data: d }) => {
      setData(d);
      if (d.access) {
        const p = d.data || {};
        setAnswers(p.answers || {});
        setCareers(p.careers || {});
        setAi(p.ai || null);
        setIdx(Math.min(p.current_step || 0, (d.steps || []).length - 1));
      }
    }).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message));
    // eslint-disable-next-line
  }, []);

  const steps = data?.steps || [];
  const step = steps[idx];
  const total = steps.length;
  const jobFields = data?.job_fields || [];

  const answeredCount = useMemo(() => steps.filter((s) => {
    if (s.type === "career") return ((careers[s.career]?.jobs) || []).some((j) => (j?.position || "").trim());
    const v = answers[s.key];
    return Array.isArray(v) ? v.length > 0 : !!v;
  }).length, [steps, answers, careers]);

  const persist = async (extra = {}) => {
    setSaving(true);
    try {
      await api.put("/profiling/me", { answers, careers, current_step: idx, ...extra }, q);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  const go = async (dir) => {
    await persist({ current_step: Math.max(0, Math.min(total - 1, idx + dir)) });
    setIdx((i) => Math.max(0, Math.min(total - 1, i + dir)));
    setSlot(0);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const setJob = (careerKey, slotIdx, field, val) => {
    setCareers((prev) => {
      const cur = prev[careerKey] || { title: "", jobs: [] };
      const jobs = [...(cur.jobs || [])];
      while (jobs.length < (data?.slots || 3)) jobs.push({});
      jobs[slotIdx] = { ...(jobs[slotIdx] || {}), [field]: val };
      return { ...prev, [careerKey]: { ...cur, jobs } };
    });
  };

  const fetchJob = async (careerKey, slotIdx) => {
    const url = (careers[careerKey]?.jobs?.[slotIdx]?.link || "").trim();
    if (!url) { toast.error("Bitte zuerst den Link zur Stellenanzeige einfügen"); return; }
    setFetching(`${careerKey}-${slotIdx}`);
    try {
      await persist();
      const { data: res } = await api.post("/profiling/me/fetch-job",
        { career: careerKey, slot: slotIdx, url }, { timeout: 180000, ...q });
      setCareers((prev) => {
        const cur = prev[careerKey] || { title: "", jobs: [] };
        const jobs = [...(cur.jobs || [])];
        jobs[slotIdx] = {
          ...(jobs[slotIdx] || {}), link: res.link || url,
          position: (jobs[slotIdx]?.position || "").trim() || res.position || "",
          requirements: res.requirements || jobs[slotIdx]?.requirements || "",
        };
        return { ...prev, [careerKey]: { ...cur, jobs } };
      });
      toast.success("Anforderungen aus der Anzeige übernommen – bitte prüfen und ergänzen");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setFetching(null); }
  };

  const generate = async () => {
    setGenerating(true);
    try {
      await persist();
      const { data: res } = await api.post("/profiling/me/generate", {}, { timeout: 300000, ...q });
      setAi(res.ai);
      setIdx(total);
      toast.success("Dein Kompetenzabgleich ist fertig");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setGenerating(false); }
  };

  const openPdf = async (kind) => {
    try {
      const r = await api.get("/profiling/me/pdf", { responseType: "blob", timeout: 300000, params: { kind, ...(forClient ? { client_id: forClient } : {}) } });
      window.open(URL.createObjectURL(new Blob([r.data], { type: "application/pdf" })), "_blank", "noopener");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  if (!data) return <Shell><p className="text-sm text-slate-500">Lädt…</p></Shell>;
  if (!data.access) {
    return (
      <Shell>
        <h1 className="font-heading text-3xl font-light tracking-tighter text-slate-900">Profiling</h1>
        <p className="text-sm text-slate-600 mt-3" data-testid="profiling-no-access">
          {forClient
            ? "Für diesen Teilnehmer ist das Profiling noch nicht freigeschaltet – im Reiter „Profiling“ der Akte kannst du es mit einem Klick freischalten."
            : "Für dich ist das Profiling noch nicht freigeschaltet. Sprich deinen Coach an – er kann dir den Zugang freigeben."}
        </p>
      </Shell>
    );
  }

  // ---------- Abschlussseite ----------
  if (idx >= total) {
    return (
      <Shell wide>
        <div className="text-center py-6" data-testid="profiling-finish">
          <Target size={38} strokeWidth={1} className="mx-auto text-slate-900 mb-4" />
          <h1 className="font-heading text-4xl font-light tracking-tighter text-slate-900">Dein Profil steht</h1>
          {ai?.headline && <p className="text-base text-slate-800 mt-3">{ai.headline}</p>}
          {ai?.claim && <p className="text-sm text-slate-600 italic mt-1">„{String(ai.claim).replace(/^[„"]|[“"]$/g, "")}“</p>}
          <div className="flex gap-3 justify-center mt-8 flex-wrap">
            <button onClick={() => openPdf("summary")} data-testid="profiling-pdf-summary"
              className="px-5 py-3 bg-slate-900 text-white text-sm font-medium hover:bg-black flex items-center gap-2">
              <FileText size={14} strokeWidth={1.5} /> Stellenanalyse & Abgleich (PDF)
            </button>
            <button onClick={() => openPdf("profile")} data-testid="profiling-pdf-profile"
              className="px-5 py-3 border border-slate-900 text-sm font-medium hover:bg-slate-100 flex items-center gap-2">
              <Download size={14} strokeWidth={1.5} /> Ein-Seiten-Profil (PDF)
            </button>
            <button onClick={() => setIdx(0)} data-testid="profiling-edit-again"
              className="px-5 py-3 border border-slate-300 text-sm font-medium hover:bg-slate-100">Antworten überarbeiten</button>
            <button onClick={() => navigate(forClient ? `/avgs/${forClient}?tab=profiling` : "/mein-bereich")}
              className="px-5 py-3 text-sm text-slate-600 hover:text-slate-900">
              {forClient ? "Zurück zur Akte" : "Zurück zu meinem Bereich"}
            </button>
          </div>

          {ai && (
            <div className="text-left mt-12 border-t border-slate-200 pt-8 grid gap-8 sm:grid-cols-2" data-testid="profiling-result">
              <Block title="Das bringst du bereits mit" items={ai.overall_matches} />
              <Block title="Das fehlt dir noch" items={ai.overall_gaps} />
              <Block title="Dein Mehrwert" items={ai.value_add} />
              <Block title="Empfehlung" text={ai.recommendation} />
              {(ai.careers || []).map((cc) => (
                <div key={cc.key} className="sm:col-span-2 border border-slate-200 p-5" data-testid={`profiling-career-result-${cc.key}`}>
                  <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">
                    {(data.career_defs.find((x) => x.key === cc.key) || {}).label}
                  </div>
                  <h3 className="font-heading text-lg font-light tracking-tight text-slate-900 mt-1">{cc.title}</h3>
                  <div className="grid gap-6 sm:grid-cols-3 mt-4">
                    <Block title="Passt bereits" items={cc.matches} small />
                    <Block title="Noch offen" items={cc.gaps} small />
                    <Block title="Portfolio-To-dos" items={cc.portfolio} small />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Shell>
    );
  }

  const value = answers[step.key];
  const career = step.type === "career" ? (careers[step.career] || { title: "", jobs: [] }) : null;
  const jobs = career ? (career.jobs || []) : [];
  const job = jobs[slot] || {};

  return (
    <Shell wide>
      <div className="flex items-center justify-between gap-4 mb-8">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">
            Profiling · Schritt {idx + 1} von {total}
          </div>
          <div className="text-xs text-slate-500 mt-1">{answeredCount} von {total} ausgefüllt{saving ? " · speichert…" : ""}</div>
        </div>
        <button onClick={() => navigate(forClient ? `/avgs/${forClient}?tab=profiling` : "/mein-bereich")}
          className="text-slate-400 hover:text-slate-700" aria-label="Schließen" data-testid="profiling-close">
          <X size={18} strokeWidth={1.5} />
        </button>
      </div>
      {forClient && (
        <div className="border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-900 mb-6" data-testid="profiling-staff-mode">
          Begleit-Modus: Du bearbeitest das Profiling von <b>{data.client_name}</b>. Alles, was du hier einträgst, steht auch im Portal des Teilnehmers.
        </div>
      )}
      <div className="h-[3px] bg-slate-200 mb-10">
        <motion.div className="h-full bg-slate-900" animate={{ width: `${((idx + 1) / total) * 100}%` }} transition={{ duration: 0.4 }} />
      </div>

      <AnimatePresence mode="wait">
        <motion.div key={step.key} initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }}
          transition={{ duration: 0.3 }} data-testid={`profiling-step-${step.key}`}>
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">{step.chapter}</div>
          <h1 className="font-heading text-4xl sm:text-5xl font-light tracking-tighter text-slate-900 mt-2">{step.title}</h1>
          <p className="text-base text-slate-700 mt-4 max-w-2xl">{step.question}</p>

          <div className="mt-8">
            {step.type === "text" && (
              <textarea data-testid="profiling-input-text" rows={4} value={value || ""} placeholder={step.placeholder || ""}
                onChange={(e) => setAnswers((p) => ({ ...p, [step.key]: e.target.value }))}
                onBlur={() => persist()}
                className="w-full max-w-2xl border border-slate-300 p-4 text-base focus:outline-none focus:border-slate-900" />
            )}

            {step.type === "chips" && (
              <ChipInput value={Array.isArray(value) ? value : []} max={step.max} placeholder={step.placeholder}
                onChange={(v) => setAnswers((p) => ({ ...p, [step.key]: v }))} onBlur={() => persist()} />
            )}

            {step.type === "lines" && (
              <LineList value={Array.isArray(value) ? value : []} max={step.max} placeholder={step.placeholder}
                onChange={(v) => setAnswers((p) => ({ ...p, [step.key]: v }))} onBlur={() => persist()} />
            )}

            {step.type === "career" && (
              <div>
                <label className="block max-w-2xl">
                  <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Berufsbild / Zielposition</span>
                  <input data-testid={`profiling-career-title-${step.career}`} value={career.title || ""}
                    placeholder="z. B. Projektmanagerin Kultur & Soziales"
                    onChange={(e) => setCareers((p) => ({ ...p, [step.career]: { ...career, title: e.target.value } }))}
                    onBlur={() => persist()}
                    className="w-full border border-slate-300 px-4 py-3 text-sm mt-2 focus:outline-none focus:border-slate-900" />
                </label>

                <div className="flex gap-2 mt-8 border-b border-slate-200">
                  {Array.from({ length: data.slots || 3 }).map((_, i) => {
                    const filled = (jobs[i]?.position || "").trim();
                    return (
                      <button key={i} onClick={() => setSlot(i)} data-testid={`profiling-slot-${step.career}-${i}`}
                        className={`px-5 py-3 text-sm -mb-px border-b-2 transition-colors ${slot === i ? "border-slate-900 text-slate-900 font-medium" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
                        Stelle {i + 1}{filled ? " ·" : ""}
                      </button>
                    );
                  })}
                </div>

                <div className="mt-6 space-y-5">
                  {jobFields.map((f) => (
                    <div key={f.key}>
                      <div className="flex items-end justify-between gap-3 flex-wrap">
                        <div>
                          <div className="text-sm font-medium text-slate-900">{f.label}</div>
                          <div className="text-xs text-slate-500 mt-0.5 max-w-xl">{f.hint}</div>
                        </div>
                        {f.key === "link" && (
                          <button onClick={() => fetchJob(step.career, slot)} disabled={fetching === `${step.career}-${slot}`}
                            data-testid={`profiling-fetch-${step.career}-${slot}`}
                            className="px-4 py-2 border border-slate-900 text-xs font-medium hover:bg-slate-100 disabled:opacity-50 flex items-center gap-1.5">
                            {fetching === `${step.career}-${slot}` ? <Loader2 size={12} className="animate-spin" /> : <Link2 size={12} strokeWidth={1.5} />}
                            {fetching === `${step.career}-${slot}` ? "Liest Anzeige…" : "Anforderungen aus Link holen"}
                          </button>
                        )}
                      </div>
                      {f.type === "long" ? (
                        <textarea rows={4} value={job[f.key] || ""} data-testid={`profiling-field-${f.key}`}
                          onChange={(e) => setJob(step.career, slot, f.key, e.target.value)} onBlur={() => persist()}
                          className="w-full border border-slate-300 p-3 text-sm mt-2 focus:outline-none focus:border-slate-900" />
                      ) : (
                        <input value={job[f.key] || ""} data-testid={`profiling-field-${f.key}`}
                          placeholder={f.key === "link" ? "https://…" : ""}
                          onChange={(e) => setJob(step.career, slot, f.key, e.target.value)} onBlur={() => persist()}
                          className="w-full border border-slate-300 px-4 py-3 text-sm mt-2 focus:outline-none focus:border-slate-900" />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </AnimatePresence>

      <div className="flex items-center justify-between gap-4 mt-14 pt-6 border-t border-slate-200">
        <button onClick={() => go(-1)} disabled={idx === 0} data-testid="profiling-back"
          className="px-4 py-2.5 text-sm text-slate-600 hover:text-slate-900 disabled:opacity-30 flex items-center gap-2">
          <ArrowLeft size={14} strokeWidth={1.5} /> Zurück
        </button>
        {idx < total - 1 ? (
          <button onClick={() => go(1)} data-testid="profiling-next"
            className="px-6 py-3 bg-slate-900 text-white text-sm font-medium hover:bg-black flex items-center gap-2">
            Weiter <ArrowRight size={14} strokeWidth={1.5} />
          </button>
        ) : (
          <div className="flex items-center gap-2 flex-wrap justify-end">
            {ai && (
              <button onClick={() => setIdx(total)} data-testid="profiling-open-result"
                className="px-5 py-3 border border-slate-900 text-sm font-medium hover:bg-slate-100 flex items-center gap-2">
                Zum fertigen Profil <ArrowRight size={14} strokeWidth={1.5} />
              </button>
            )}
            <button onClick={generate} disabled={generating} data-testid="profiling-generate"
              className="px-6 py-3 bg-slate-900 text-white text-sm font-medium hover:bg-black disabled:opacity-50 flex items-center gap-2">
              {generating ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} strokeWidth={1.5} />}
              {generating ? "Abgleich läuft…" : ai ? "Abgleich neu erstellen" : "Kompetenzabgleich erstellen"}
            </button>
          </div>
        )}
      </div>
    </Shell>
  );
}

const Block = ({ title, items, text, small }) => (
  <div>
    <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 mb-2">{title}</div>
    {text ? <p className={`${small ? "text-xs" : "text-sm"} text-slate-700 leading-relaxed`}>{text}</p> : (
      <ul className={`${small ? "text-xs" : "text-sm"} text-slate-700 space-y-1.5`}>
        {(items || []).map((it, i) => (
          <li key={i} className="flex gap-2"><span className="text-slate-400">·</span><span>{String(it)}</span></li>
        ))}
        {!(items || []).length && <li className="text-slate-400">—</li>}
      </ul>
    )}
  </div>
);

const ChipInput = ({ value, max, placeholder, onChange, onBlur }) => {
  const [draft, setDraft] = useState("");
  const add = () => {
    const v = draft.trim();
    if (!v || value.includes(v) || value.length >= (max || 10)) { setDraft(""); return; }
    onChange([...value, v]);
    setDraft("");
  };
  return (
    <div className="max-w-2xl">
      <div className="flex flex-wrap gap-2 mb-3">
        {value.map((w) => (
          <span key={w} data-testid={`profiling-chip-${w}`} className="px-3 py-1.5 bg-slate-100 text-sm text-slate-800 flex items-center gap-2">
            {w}
            <button onClick={() => onChange(value.filter((x) => x !== w))} aria-label={`${w} entfernen`}><X size={11} strokeWidth={2} /></button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <input value={draft} placeholder={placeholder || "Stichwort eingeben"} data-testid="profiling-chip-input"
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); add(); } }}
          onBlur={() => { add(); onBlur?.(); }}
          className="flex-1 border border-slate-300 px-4 py-3 text-sm focus:outline-none focus:border-slate-900" />
        <button onClick={add} data-testid="profiling-chip-add" className="px-4 border border-slate-300 hover:bg-slate-100">
          <Plus size={14} strokeWidth={1.5} />
        </button>
      </div>
      <div className="text-xs text-slate-500 mt-2">{value.length} von {max} · mit Enter hinzufügen</div>
    </div>
  );
};

const LineList = ({ value, max, placeholder, onChange, onBlur }) => {
  const [draft, setDraft] = useState("");
  const add = () => {
    const v = draft.trim();
    if (!v || value.length >= (max || 10)) { setDraft(""); return; }
    onChange([...value, v]);
    setDraft("");
  };
  return (
    <div className="max-w-2xl">
      <ul className="space-y-2 mb-3">
        {value.map((w, i) => (
          <li key={`${w}-${i}`} className="flex items-start gap-2 border border-slate-200 px-4 py-2.5" data-testid={`profiling-line-${i}`}>
            <span className="text-sm text-slate-800 flex-1">{w}</span>
            <button onClick={() => onChange(value.filter((_, x) => x !== i))} aria-label="Eintrag entfernen"
              className="text-slate-400 hover:text-slate-900"><X size={12} strokeWidth={2} /></button>
          </li>
        ))}
      </ul>
      <div className="flex gap-2">
        <input value={draft} placeholder={placeholder} data-testid="profiling-line-input"
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          onBlur={() => { add(); onBlur?.(); }}
          className="flex-1 border border-slate-300 px-4 py-3 text-sm focus:outline-none focus:border-slate-900" />
        <button onClick={add} data-testid="profiling-line-add" className="px-4 border border-slate-300 hover:bg-slate-100">
          <Plus size={14} strokeWidth={1.5} />
        </button>
      </div>
      <div className="text-xs text-slate-500 mt-2">{value.length} von {max} · mit Enter hinzufügen</div>
    </div>
  );
};

const Shell = ({ children, wide }) => (
  <div className="min-h-screen bg-white px-5 py-10 sm:px-10">
    <div className={`${wide ? "max-w-4xl" : "max-w-2xl"} mx-auto`}>{children}</div>
  </div>
);
